import { FC } from "react";
import Link from "next/link";
import { ExternalLinkIcon } from "lucide-react";

import { clsxMerge } from "shared/lib/helpers";
import CenteredSpinner from "shared/ui/spinners/centered-spinner";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";
import { AccountDetailsSectionContainer } from "shared/ui/account-details/component-wrapper";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";

interface AccountAboutSectionPropsI {
  className?: string;
}

const ABOUT_ACCOUNT_DATA_KEYS = {
  RECENT_NEWS: "recent_news",
  COMPANY_SIZE: "company_size",
  COMPANY_PURPOSE: "company_purpose",
} as const;

const ABOUT_ACCOUNT_DATA = {
  [ABOUT_ACCOUNT_DATA_KEYS.RECENT_NEWS]: (companyName: string) => ({
    label: "Recent news",
    searchQuery: `https://www.google.com/search?q=${companyName}%20recent%20news&tbm=nws`,
  }),
  [ABOUT_ACCOUNT_DATA_KEYS.COMPANY_SIZE]: (companyName: string) => ({
    label: "Company size",
    searchQuery: `https://www.google.com/search?q=${companyName}%20crunchbase`,
  }),
  [ABOUT_ACCOUNT_DATA_KEYS.COMPANY_PURPOSE]: (companyName: string) => ({
    label: "Company purpose",
    searchQuery: `https://www.google.com/search?q=what%20does%20${companyName}%20do`,
  }),
};

export const AccountAboutSection: FC<AccountAboutSectionPropsI> = ({
  className,
}) => {
  const { account, accountDetailsApi } = useAccountDetailsContext();
  const { name: accountName } = account || {};

  return (
    <div className={clsxMerge(className)}>
      <AccountDetailsSectionContainer title="About account">
        <DataStatesWrapper
          viewName="company-purpose"
          api={accountDetailsApi}
          loading={<CenteredSpinner className="h-[140px]" />}
        >
          <div className="typography-body-4">
            {Object.values(ABOUT_ACCOUNT_DATA_KEYS).map((dataKey) => {
              const { label, searchQuery } = ABOUT_ACCOUNT_DATA[dataKey](
                accountName || ""
              );

              return (
                <div key={dataKey}>
                  <Link
                    href={searchQuery}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ae-link mb-1.5 flex items-center gap-1 hover:underline"
                  >
                    {label}
                    <ExternalLinkIcon className="size-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </DataStatesWrapper>
      </AccountDetailsSectionContainer>
    </div>
  );
};
